import { useExtraData } from "app/hooks";
import { useAnalytics } from "app/hooks/shared/analytics/useAnalytics";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import { isVerticalProfileExtra } from "entities/configurator/model/staander";
import "app/styles/extra-options.scss";
import { useEffect } from "react";
import { OptionTemplate } from "shared/api/middleware-api-client";
import { toggleActivateKozijnColor } from "shared/store/kozijn/kozijnStore";

const disableSelfAssemblyOption = (
  extraOptionCode: string,
  selectedDoor: OptionTemplate,
) => {
  // For every self assembly door, disable "scharniervoorbereidingopdekdeur"
  if (extraOptionCode === "scharniervoorbereidingopdekdeur") {
    return true;
  }

  // For every self assembly opdekdeur, disable "bovenlichtrook"
  if (
    (extraOptionCode === "bovenlichtrook" ||
      extraOptionCode === "bovenlicht") &&
    selectedDoor.code === "opdekdeur"
  ) {
    return true;
  }

  // For every self assembly taatsdeur or schuifdeur, disable verticaalstelprofielleft and verticaalstelprofielright
  if (
    (extraOptionCode === "verticaalstelprofielleft" ||
      extraOptionCode === "verticaalstelprofielright") &&
    (selectedDoor.code === "taatsdeur" || selectedDoor.code === "schuifdeur")
  ) {
    return true;
  }

  return false;
};

export function useExtraHelper() {
  const {
    selfAssembleDoorData,
    extra,
    updateExtra,
    isExtraChecked,
    selectedDoor,
    color,
    glass,
    getConfigurationProduct,
    isOpdekOrStomp,
  } = useConfigurationOptionsContext();

  const { includedExtraOptions, isExtraDataLoading } = useExtraData(
    selectedDoor?.code,
    color,
    glass,
  );

  const extraOptions =
    includedExtraOptions?.filter((option) => {
      // If the door is SelfAssembly => disable option Scharniervoorbereiding
      if (
        selfAssembleDoorData.selfAssembly &&
        disableSelfAssemblyOption(option.code, selectedDoor!)
      ) {
        return false;
      }

      if (option.code.includes("breedte")) {
        return false;
      }
      if (isVerticalProfileExtra(option.code)) {
        return false;
      }
      return true;
    }) ?? [];

  const { onConfig } = useAnalytics();

  const selectedExtras = extra || [];

  useEffect(() => {
    swapBovenlicht();
  }, [includedExtraOptions]);

  const swapBovenlicht = () => {
    if (!extra || !extra.length) return;
    if (!includedExtraOptions || !includedExtraOptions.length) return;

    const oldBovenlicht = extra.find((option) =>
      option.code.includes("bovenlicht"),
    );
    const newBovenlicht = includedExtraOptions.find((option) =>
      option.code.includes("bovenlicht"),
    );

    if (
      oldBovenlicht &&
      newBovenlicht &&
      oldBovenlicht.code === newBovenlicht.code
    )
      return;

    let newExtra = [...extra];

    if (oldBovenlicht)
      newExtra = newExtra.filter(
        (option) => option.code.includes("bovenlicht") === false,
      );
    if (newBovenlicht) newExtra.push(newBovenlicht);

    updateExtra(newExtra);
  };

  const onExtraChange = (item: OptionTemplate) => {
    let newExtraOptions = extra || [];
    // Check or uncheck item
    if (!isExtraChecked(extra, item)) {
      newExtraOptions.push(item);
    } else {
      newExtraOptions.splice(
        newExtraOptions.findIndex((option) => option.id === item.id),
        1,
      );
    }

    updateExtra(newExtraOptions);

    const product = getConfigurationProduct();
    if (product)
      onConfig({
        ...product,
        extraOptions: newExtraOptions.map((option) => {
          return { id: option.id, amount: option.amount };
        }),
        extraOptionsNames: newExtraOptions.map((option) => option.name),
      });

    // Toggle kozijn change if selected option is kozijn color extra option
    if (isOpdekOrStomp && item.code.includes("verfset")) {
      toggleActivateKozijnColor();
    }
  };

  const handleAmountChange = (
    changedOption: OptionTemplate,
    newAmount: number,
  ) => {
    let chosenAmount = Math.max(1, Math.min(newAmount, 100));
    const updatedExtras = selectedExtras.map((option) =>
      option.id === changedOption.id
        ? { ...option, amount: chosenAmount }
        : option,
    );
    updateExtra(updatedExtras);
  };

  useEffect(() => {
    if (!extra) return;

    if (extra.findIndex((option) => option.code.includes("verfset")) > -1) {
      toggleActivateKozijnColor();
    }
  }, [extra]);

  return {
    extraOptions,
    isExtraDataLoading,
    onExtraChange,
    isExtraChecked,
    handleAmountChange,
    selectedExtras,
    extra,
  };
}
